import { useMutation, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { announcementApi } from 'src/Services/API';
import {
  AssetAnnouncementDto,
  CreateAssetAnnouncementDto,
  UpdateAssetAnnouncementDto,
} from 'src/types/announcement.types';

const invalidate = (queryClient: ReturnType<typeof useQueryClient>, assetId?: string) => {
  queryClient.invalidateQueries({ queryKey: ['asset-announcements', assetId] });
  queryClient.invalidateQueries({ queryKey: ['active-asset-announcement', assetId] });
};

export const useCreateAssetAnnouncement = () => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();
  return useMutation<AssetAnnouncementDto, unknown, CreateAssetAnnouncementDto>(
    dto => announcementApi.createForAsset(assetId!, dto),
    { onSuccess: () => invalidate(queryClient, assetId) }
  );
};

export const useUpdateAssetAnnouncement = () => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();
  return useMutation<
    AssetAnnouncementDto,
    unknown,
    { id: string; dto: UpdateAssetAnnouncementDto }
  >(({ id, dto }) => announcementApi.updateForAsset(assetId!, id, dto), {
    onSuccess: () => invalidate(queryClient, assetId),
  });
};

export const usePublishAssetAnnouncement = () => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();
  return useMutation<AssetAnnouncementDto, unknown, string>(
    id => announcementApi.publishForAsset(assetId!, id),
    { onSuccess: () => invalidate(queryClient, assetId) }
  );
};

export const useUnpublishAssetAnnouncement = () => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();
  return useMutation<AssetAnnouncementDto, unknown, string>(
    id => announcementApi.unpublishForAsset(assetId!, id),
    { onSuccess: () => invalidate(queryClient, assetId) }
  );
};

export const useDeleteAssetAnnouncement = () => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();
  return useMutation<void, unknown, string>(id => announcementApi.deleteForAsset(assetId!, id), {
    onSuccess: () => invalidate(queryClient, assetId),
  });
};
